import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Card, Col, Collapse, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Spin,
    Statistic, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, InboxOutlined, PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtNum, fmtTime, ok, toastErr } from './common';
import { StreamRef } from './opsCommon';
import StreamOpsDrawer from './StreamOpsDrawer';

const { Text, Paragraph } = Typography;

/** 营销内容识别（api/docs/tm-marketing-classifier.md §10/§11）。后台固定简体中文。 */

const LABELS = { marketing: ['purple', '营销'], transactional: ['blue', '非营销'] };
const SOURCES = { tm: '发信', upload: '上传', paste: '粘贴' };
const LABEL_SOURCE = { auto_stream: '自动·通道', auto_rule: '自动·规则', admin: '人工' };
const LABEL_OPTIONS = [{ value: 'marketing', label: '营销' }, { value: 'transactional', label: '非营销' }, { value: '', label: '不标注' }];

const asList = (v) => (Array.isArray(v) ? v : typeof v === 'string' && v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
const parseTokens = (v) => {
    if (Array.isArray(v)) return v;
    try { const j = JSON.parse(v || '[]'); return Array.isArray(j) ? j : []; } catch (_) { return []; }
};
const frac = (v) => (v === null || v === undefined || v === '' ? null : Number(v) > 1 ? Number(v) / 100 : Number(v));
const pctText = (v) => (v === null ? '-' : `${(v * 100).toFixed(1)}%`);
const score = (v) => (v === null || v === undefined || Number(v) < 0 ? '-' : Number(v).toFixed(3));
const userText = (u, s) => (u && typeof u === 'object' ? `#${u.id ?? ''} ${u.username || u.email || ''}` : u || (s.user_id ? `#${s.user_id}` : ''));

/** 统计接口字段宽松归一（后端形状以契约 §10 为准，这里兼容几种写法） */
const normStats = (d = {}) => {
    const lab = d.samples?.by_label || d.samples || d.labels || d.counts || {};
    const model = d.model || {};
    const acc = d.accuracy_7d || d.accuracy || {};
    const accOf = (x) => (x && typeof x === 'object' ? frac(x.accuracy ?? x.acc) : frac(x));
    const top = d.streams_24h || d.top_streams || d.stream_ratio_24h || d.stream_ratio || [];
    const thr = d.throttled_streams ?? d.throttled;
    return {
        mkt: lab.marketing ?? 0,
        txn: lab.transactional ?? 0,
        unlabeled: lab.unlabeled ?? lab.none ?? null,
        ready: !!(model.ready ?? d.ready),
        version: model.version ?? d.version ?? null,
        minDocs: model.min_docs ?? d.min_docs ?? null,
        accModel: accOf(acc.model),
        accRule: accOf(acc.rule),
        accN: acc.n ?? acc.total ?? acc.model?.n ?? null,
        top: Array.isArray(top) ? top : [],
        throttled: Array.isArray(thr) ? thr.length : thr ?? (Array.isArray(top) ? top.filter((r) => r.throttled).length : 0),
    };
};

const normSettings = (d) => {
    if (!d) return {};
    const src = d.items || d.settings || d;
    if (Array.isArray(src)) return Object.fromEntries(src.map((s) => [s.key, s.value]));
    return src;
};

const LabelButtons = ({ value, onChange }) => (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
        {['marketing', 'transactional'].map((k) => (
            <Button key={k} size="small" type={value === k ? 'primary' : 'default'} danger={k === 'marketing' && value === k}
                onClick={() => onChange(value === k ? null : k)}>{LABELS[k][1]}</Button>
        ))}
    </Space>
);

const SampleDrawer = ({ id, onClose, onDeleted }) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        setD(null);
        if (!id) return;
        tmAdminExtService.mktSample(id).then((r) => setD(r?.data || {})).catch((e) => toastErr(e, '加载失败'));
    }, [id]);
    const s = d?.sample || d || {};
    const top = d?.current_model?.top || d?.top || d?.contributors || s.top || [];
    const tokens = parseTokens(s.tokens);
    const rawUrl = d?.raw_url || s.raw_url;
    const remove = async () => {
        try {
            const r = await tmAdminExtService.mktDelete(id);
            if (ok(r)) { message.success('已删除'); onDeleted(id); }
        } catch (e) { toastErr(e, '删除失败'); }
    };
    return (
        <Drawer open={!!id} onClose={onClose} width={720} title={s.subject || `#${id}`} destroyOnClose
            extra={d && (
                <Space>
                    {rawUrl && <Button icon={<DownloadOutlined />} href={rawUrl} target="_blank" rel="noopener noreferrer">.eml</Button>}
                    <Popconfirm title="删除这个样本？" onConfirm={remove}><Button danger icon={<DeleteOutlined />} /></Popconfirm>
                </Space>
            )}>
            {!d ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions size="small" column={2} bordered items={[
                        { key: 'src', label: '来源', children: SOURCES[s.source] || s.source || '-' },
                        { key: 'ct', label: '时间', children: fmtTime(s.created_at) },
                        { key: 'rs', label: '规则分', children: s.rule_score ?? '-' },
                        { key: 'ms', label: '模型分', children: score(s.model_score) },
                        { key: 'sig', label: '信号', span: 2, children: asList(s.signals).map((x) => <Tag key={x}>{x}</Tag>) },
                    ]} />
                    {top.length > 0 && (
                        <Card size="small" title="关键词">
                            <Space size={[4, 6]} wrap>
                                {top.map((x) => {
                                    const w = Number(x.w ?? x.weight ?? 0);
                                    return <Tag key={x.t ?? x.token} color={w >= 0 ? 'purple' : 'blue'}>{x.t ?? x.token} {w >= 0 ? '+' : ''}{w.toFixed(2)}</Tag>;
                                })}
                            </Space>
                        </Card>
                    )}
                    <Card size="small" title="正文">
                        <Paragraph style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 420, overflow: 'auto', margin: 0, fontSize: 13 }}>
                            {s.text || '-'}
                        </Paragraph>
                    </Card>
                    {tokens.length > 0 && (
                        <Collapse ghost size="small" items={[{
                            key: 'tk', label: `tokens (${tokens.length})`,
                            children: <Space size={[4, 4]} wrap>{tokens.map((x) => <Tag key={x} style={{ fontSize: 11 }}>{x}</Tag>)}</Space>,
                        }]} />
                    )}
                </Space>
            )}
        </Drawer>
    );
};

const AddModal = ({ open, onClose, onDone }) => {
    const [tab, setTab] = useState('upload');
    const [label, setLabel] = useState('');
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => { if (open) { setFiles([]); form.resetFields(); } }, [open, form]);

    const submit = async () => {
        let r;
        setBusy(true);
        try {
            if (tab === 'upload') {
                if (!files.length) { message.warning('请选择文件'); return; }
                r = await tmAdminExtService.mktUpload(files, label);
            } else {
                const v = await form.validateFields();
                r = await tmAdminExtService.mktPaste({ subject: v.subject || '', text: v.text, label: label || null });
            }
            if (ok(r)) {
                const n = r.data?.created ?? r.data?.count ?? (Array.isArray(r.data?.items) ? r.data.items.length : null);
                message.success(n !== null && n !== undefined ? `已添加 ${n} 个` : '已添加');
                onDone();
            }
        } catch (e) {
            if (!e?.errorFields) toastErr(e, '添加失败');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} onCancel={onClose} onOk={submit} confirmLoading={busy} title="添加样本" okText="添加" width={560} destroyOnClose>
            <Tabs activeKey={tab} onChange={setTab} items={[
                {
                    key: 'upload', label: '上传',
                    children: (
                        <Upload.Dragger multiple accept=".eml,.mbox,.zip" fileList={files.map((f) => ({ uid: f.uid, name: f.name, status: 'done' }))}
                            beforeUpload={(f) => { setFiles((l) => [...l, f]); return false; }}
                            onRemove={(f) => setFiles((l) => l.filter((x) => x.uid !== f.uid))}>
                            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                            <p className="ant-upload-text">.eml / .mbox / .zip</p>
                            <p className="ant-upload-hint">≤ 20MB</p>
                        </Upload.Dragger>
                    ),
                },
                {
                    key: 'paste', label: '粘贴',
                    children: (
                        <Form form={form} layout="vertical">
                            <Form.Item name="subject" label="主题"><Input maxLength={500} /></Form.Item>
                            <Form.Item name="text" label="正文" rules={[{ required: true, message: '请输入正文' }]}>
                                <Input.TextArea autoSize={{ minRows: 6, maxRows: 14 }} />
                            </Form.Item>
                        </Form>
                    ),
                },
            ]} />
            <Space style={{ marginTop: 12 }}>
                <Text>标注</Text>
                <Select value={label} onChange={setLabel} options={LABEL_OPTIONS} style={{ width: 120 }} />
            </Space>
        </Modal>
    );
};

const SETTING_KEYS = ['mkt_enabled', 'mkt_prob_threshold', 'mkt_rule_threshold', 'mkt_sample_rate', 'mkt_min_docs', 'mkt_auto_learn_stream', 'mkt_auto_learn_rule0'];
const SWITCHES = new Set(['mkt_enabled', 'mkt_auto_learn_stream', 'mkt_auto_learn_rule0']);
const truthy = (v) => v === true || v === 1 || v === '1' || v === 'true';

const SettingsPanel = ({ onRebuilt }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminExtService.mktSettings();
            const s = normSettings(r?.data);
            form.setFieldsValue(Object.fromEntries(SETTING_KEYS.map((k) => [k, SWITCHES.has(k) ? truthy(s[k]) : (s[k] === null || s[k] === undefined || s[k] === '' ? null : Number(s[k]))])));
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [form]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const v = await form.validateFields();
        const values = Object.fromEntries(SETTING_KEYS.map((k) => [k, SWITCHES.has(k) ? (v[k] ? 1 : 0) : v[k]]));
        setSaving(true);
        try {
            const r = await tmAdminExtService.mktSaveSettings(values);
            if (ok(r)) { message.success('已保存'); load(); }
        } catch (e) {
            toastErr(e, '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const rebuild = async () => {
        setRebuilding(true);
        try {
            const r = await tmAdminExtService.mktRebuild();
            if (ok(r)) { message.success(r.data?.version ? `已重建 v${r.data.version}` : '已重建'); onRebuilt(); }
        } catch (e) {
            toastErr(e, '重建失败');
        } finally {
            setRebuilding(false);
        }
    };

    const tip = (label, t) => <Tooltip title={t}><span>{label}</span></Tooltip>;
    return (
        <Spin spinning={loading}>
            <Form form={form} layout="inline" style={{ rowGap: 12 }}>
                <Form.Item name="mkt_enabled" label="启用" valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="mkt_prob_threshold" label={tip('模型阈值', '模型概率 ≥ 此值判为营销')}>
                    <InputNumber min={0.5} max={0.999} step={0.01} style={{ width: 100 }} />
                </Form.Item>
                <Form.Item name="mkt_rule_threshold" label={tip('规则阈值', '模型未就绪时，规则分 ≥ 此值判为营销')}>
                    <InputNumber min={1} max={20} precision={0} style={{ width: 90 }} />
                </Form.Item>
                <Form.Item name="mkt_sample_rate" label={tip('抽样率', '未命中的信按此比例上报正文样本')}>
                    <InputNumber min={0} max={1} step={0.01} style={{ width: 100 }} />
                </Form.Item>
                <Form.Item name="mkt_min_docs" label={tip('最少样本', '两类各达到此数模型才就绪')}>
                    <InputNumber min={1} max={100000} precision={0} style={{ width: 100 }} />
                </Form.Item>
                <Form.Item name="mkt_auto_learn_stream" label={tip('营销通道自动标营销', '营销通道上报的样本自动标为营销')} valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="mkt_auto_learn_rule0" label={tip('事务低分自动标非营销', '事务通道且规则分 0、模型分 < 0.2 的样本自动标为非营销')} valuePropName="checked"><Switch /></Form.Item>
            </Form>
            <Space style={{ marginTop: 16 }}>
                <Button type="primary" loading={saving} onClick={save}>保存</Button>
                <Popconfirm title="立即重建模型？" onConfirm={rebuild}>
                    <Button icon={<SyncOutlined />} loading={rebuilding}>立即重建模型</Button>
                </Popconfirm>
            </Space>
        </Spin>
    );
};

const Marketing = () => {
    const [stats, setStats] = useState(normStats());
    const [statsLoading, setStatsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({});
    const [q, setQ] = useState('');
    const [openId, setOpenId] = useState(null);
    const [adding, setAdding] = useState(false);
    const [streamId, setStreamId] = useState(null);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const r = await tmAdminExtService.mktStats();
            setStats(normStats(r?.data || {}));
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries({ ...f, page: page.current, page_size: page.pageSize })
                .filter(([, v]) => v !== undefined && v !== null && v !== ''));
            const r = await tmAdminExtService.mktSamples(params);
            const d = r?.data || {};
            setRows(d.items || d.list || (Array.isArray(d) ? d : []));
            setTotal(d.total ?? 0);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [f, page]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };

    const label = async (row, next) => {
        const prev = { label: row.label, label_source: row.label_source };
        const patch = (v) => setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, ...v } : r)));
        patch({ label: next, label_source: next ? 'admin' : null });
        try {
            const r = await tmAdminExtService.mktLabel(row.id, next);
            if (!ok(r)) patch(prev);
        } catch (e) {
            patch(prev);
            toastErr(e, '标注失败', true);
        }
    };

    const columns = [
        { title: '时间', dataIndex: 'created_at', width: 150, render: fmtTime },
        { title: '来源', dataIndex: 'source', width: 64, render: (v) => SOURCES[v] || v },
        {
            title: '通道', key: 'stream', width: 150, ellipsis: true,
            render: (_, s) => (s.stream_id
                ? <Tooltip title={userText(s.user, s)}><span onClick={(e) => e.stopPropagation()}><StreamRef stream={{ id: s.stream_id, name: s.stream_name }} onOpen={setStreamId} /></span></Tooltip>
                : '-'),
        },
        {
            title: '主题', dataIndex: 'subject', ellipsis: true,
            render: (v, s) => (
                <Tooltip title={s.text ? String(s.text).slice(0, 200) : null} placement="topLeft" overlayStyle={{ maxWidth: 480 }}>
                    <span>{v || <Text type="secondary">(无主题)</Text>}</span>
                </Tooltip>
            ),
        },
        { title: '信号', dataIndex: 'signals', width: 200, render: (v) => <Space size={[2, 2]} wrap>{asList(v).map((x) => <Tag key={x} style={{ fontSize: 11, marginInlineEnd: 0 }}>{x}</Tag>)}</Space> },
        { title: '规则分', dataIndex: 'rule_score', width: 70, align: 'right', render: (v) => v ?? '-' },
        { title: '模型分', dataIndex: 'model_score', width: 76, align: 'right', render: score },
        { title: '判定', dataIndex: 'auto_flag', width: 64, render: (v) => (v === 'marketing' ? <Tag color="purple" style={{ marginInlineEnd: 0 }}>营销</Tag> : <Text type="secondary">-</Text>) },
        {
            title: '标注', key: 'label', width: 170,
            render: (_, s) => (
                <Tooltip title={s.label ? LABEL_SOURCE[s.label_source] : null}>
                    <span><LabelButtons value={s.label} onChange={(v) => label(s, v)} /></span>
                </Tooltip>
            ),
        },
    ];

    const statusTag = stats.ready
        ? <Tag color="green">就绪{stats.version ? ` v${stats.version}` : ''}</Tag>
        : <Tag color="orange">样本不足{stats.version ? ` v${stats.version}` : ''}</Tag>;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                    <Card size="small" loading={statsLoading}>
                        <Statistic title="已标注 营销 / 非营销" valueRender={() => <span>{fmtNum(stats.mkt)} / {fmtNum(stats.txn)}</span>} />
                        {stats.unlabeled !== null && <Text type="secondary" style={{ fontSize: 12 }}>未标注 {fmtNum(stats.unlabeled)}</Text>}
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={statsLoading}>
                        <Statistic title={stats.minDocs ? <Tooltip title={`两类各 ≥ ${stats.minDocs} 个已标注样本`}>模型</Tooltip> : '模型'} valueRender={() => statusTag} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={statsLoading}>
                        <Statistic title={<Tooltip title={stats.accN !== null ? `近 7 天 ${stats.accN} 个已标注样本` : '近 7 天已标注样本'}>准确率 模型 / 规则</Tooltip>}
                            valueRender={() => <span>{pctText(stats.accModel)} / {pctText(stats.accRule)}</span>} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={statsLoading}>
                        <Statistic title={<Tooltip title="事务通道 24h 营销占比 > 50%，小时上限减半">限流通道</Tooltip>} value={stats.throttled}
                            valueStyle={stats.throttled ? { color: '#cf1322' } : undefined} />
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="样本" extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => { load(); loadStats(); }} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>添加样本</Button>
                </Space>
            }>
                <Space wrap style={{ marginBottom: 12 }}>
                    <Select value={f.label ?? ''} style={{ width: 110 }} onChange={(v) => setFilter({ label: v })} options={[
                        { value: '', label: '全部' }, { value: 'none', label: '未标注' }, { value: 'marketing', label: '营销' }, { value: 'transactional', label: '非营销' },
                    ]} />
                    <Select allowClear placeholder="判定" value={f.flag} style={{ width: 110 }} onChange={(v) => setFilter({ flag: v })} options={[
                        { value: 'marketing', label: '判营销' }, { value: 'none', label: '未判营销' },
                    ]} />
                    <Select allowClear placeholder="来源" value={f.source} style={{ width: 100 }} onChange={(v) => setFilter({ source: v })}
                        options={Object.entries(SOURCES).map(([value, l]) => ({ value, label: l }))} />
                    <Input.Search allowClear placeholder="搜索主题 / 正文" style={{ width: 240 }} value={q}
                        onChange={(e) => { setQ(e.target.value); if (!e.target.value) setFilter({ q: '' }); }}
                        onSearch={(v) => setFilter({ q: v.trim() })} />
                </Space>
                <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 1150 }}
                    onRow={(s) => ({ onClick: () => setOpenId(s.id), style: { cursor: 'pointer' } })}
                    pagination={{
                        current: page.current, pageSize: page.pageSize, total, showSizeChanger: true, pageSizeOptions: [20, 50, 100],
                        onChange: (current, pageSize) => setPage({ current, pageSize }),
                    }} />
            </Card>

            <Collapse items={[{ key: 'set', label: '设置', children: <SettingsPanel onRebuilt={loadStats} /> }]} />

            <Card size="small" title="24h 事务通道营销占比">
                <Table rowKey={(r) => r.stream_id ?? r.id} size="small" loading={statsLoading} pagination={false} dataSource={stats.top.slice(0, 20)}
                    locale={{ emptyText: '无' }} scroll={{ x: 600 }}
                    columns={[
                        { title: '通道', key: 's', render: (_, r) => <StreamRef stream={{ id: r.stream_id ?? r.id, name: r.stream_name ?? r.name }} onOpen={setStreamId} /> },
                        { title: '用户', key: 'u', ellipsis: true, render: (_, r) => userText(r.user, r) || '-' },
                        { title: '营销 / 总数', key: 'n', width: 130, align: 'right', render: (_, r) => `${fmtNum(r.marketing)} / ${fmtNum(r.total)}` },
                        {
                            title: '占比', key: 'r', width: 90, align: 'right',
                            render: (_, r) => {
                                const v = frac(r.ratio ?? (r.total ? r.marketing / r.total : null));
                                return <Text type={v > 0.5 ? 'danger' : v > 0.2 ? 'warning' : undefined}>{pctText(v)}</Text>;
                            },
                        },
                        { title: '限流', dataIndex: 'throttled', width: 70, render: (v) => (v ? <Tag color="red" style={{ marginInlineEnd: 0 }}>限流</Tag> : '-') },
                    ]} />
            </Card>

            <SampleDrawer id={openId} onClose={() => setOpenId(null)}
                onDeleted={(id) => { setOpenId(null); setRows((rs) => rs.filter((r) => r.id !== id)); setTotal((t) => Math.max(0, t - 1)); loadStats(); }} />
            <AddModal open={adding} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); loadStats(); }} />
            <StreamOpsDrawer streamId={streamId} onClose={() => setStreamId(null)} onChanged={loadStats} />
        </Space>
    );
};

export default Marketing;
